import React, { useState } from 'react';
import styled from 'styled-components';

import { Label, Bubble } from '@lt/components';
import HotelCardGallery from '@lt/components/components/HotelCard/components/HotelCardGallery';

import HotelRoomMealInfo from './HotelRoomMealInfo';
import HotelRoomOffers from './HotelRoomOffers';
import EmptyGallery from './EmptyGallery';

import type { Room } from '@lt/api/dist/methods/search/types';

interface IHotelRoomCard {
  hotelRoomData: Room;
  currencySymbol?: string;
  redirectToHotelPage: (nights_count?: number) => void;
  selectedMealType?: any;
  isMobile: boolean;
  isSmall: boolean;
  // selectMealType: any;
}

const SURCHARGE_TEXT =
  'К доплатам могут относиться топливный сбор и стоимость дополнительных услуг, включённых в тур, например, новогоднего ужина или дополнительной кровати в номере. Вы видите полную финальную стоимость тура уже сейчас, однако помните, что цены на туры постоянно меняются.';

const SURCHARGE_MAYBE_TEXT =
  'К данному туру туроператор может добавить те или иные обязательные доплаты. К доплатам могут относиться топливный сбор и стоимость дополнительных услуг, включённых в тур, например, новогоднего ужина или дополнительной кровати в номере. Финальная стоимость тура станет известна на этапе выбора рейсов.';

const HotelRoomCard = (props: IHotelRoomCard): JSX.Element => {
  const {
    hotelRoomData,
    currencySymbol = '₽',
    redirectToHotelPage,
    isMobile,
    isSmall,
    // selectMealType,
  } = props;
  // const selectedMealType = props.selectedMealType || ;
  const selectedMealType =
    props.selectedMealType ||
    hotelRoomData.meal_types.sort((lhs, rhs) => lhs.min_price - rhs.min_price)[0];
  const hotelRoomOffers = hotelRoomData.offers[selectedMealType.id].filter(
    (lhs, i, offers) => offers.findIndex((rhs) => rhs.nights_count === lhs.nights_count) === i
  );
  const [surcharge, setSurcharge] = useState(0);

  return (
    hotelRoomOffers && (
      <StyledHotelRoomCardContent $isMobile={isMobile}>
        {hotelRoomData.room.images ? (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          <StyledHotelCardGallery
            images={{
              list: Object.values(
                hotelRoomData.room.images.map((image) => ({
                  jpg: image.x900,
                }))
              ),
              count: Object.keys(hotelRoomData.room.images).length,
            }}
            autoplay={{ delay: 2000, disableOnInteraction: true }}
            $isSmall={isSmall}
          />
        ) : (
          <EmptyGallery />
        )}
        <StyledContentContainer>
          <StyledHotelRoomHeading>{hotelRoomData.room.name_ru}</StyledHotelRoomHeading>
          <HotelRoomMealInfo
            // hotelRoomMealTypes={hotelRoomData.meal_types}
            // selectedMealType={selectedMealType.description}
            // selectMealType={selectMealType}
            redirectToHotelPage={redirectToHotelPage}
            selectedMealType={selectedMealType.description}
          />
          {Object.values(hotelRoomData.offers)
            .flat()[0]
            .labels?.map((label: Record<string, any>) => (
              <StyledLabelWrapper key={label.id} $isMobile={isMobile}>
                <Label
                  title={label.title}
                  description={label.description}
                  style={label.style}
                  iconComponent={label.icon_path}
                />
              </StyledLabelWrapper>
            ))}
          <HotelRoomOffers
            hotelRoomOffers={hotelRoomOffers}
            currencySymbol={currencySymbol}
            setSurcharge={setSurcharge}
            isSmall={isSmall}
          />
          <StyledSurchargeInfo>
            {surcharge !== null
              ? `Включает доплаты ${surcharge} ${currencySymbol}`
              : 'Возможны доплаты от туроператора'}
            <StyledBubble
              inline
              title=" "
              renderExtras={() => (
                <BubbleTextContainer>
                  {surcharge !== null ? SURCHARGE_TEXT : SURCHARGE_MAYBE_TEXT}
                </BubbleTextContainer>
              )}
            >
              <CircleContainer>?</CircleContainer>
            </StyledBubble>
          </StyledSurchargeInfo>
        </StyledContentContainer>
      </StyledHotelRoomCardContent>
    )
  );
};

const StyledHotelRoomCardContent = styled.div<{ $isMobile: boolean }>`
  display: flex;
  justify-content: start;
  flex-basis: ${({ $isMobile }) => ($isMobile ? 'auto' : 'calc(50% - 16px)')};

  margin: 0 8px 8px 0;

  border-radius: 4px;
  overflow: hidden;

  min-width: 300px;
  line-height: 16px;
`;

const StyledHotelCardGallery = styled(HotelCardGallery)<{ $isSmall: boolean }>`
  // повышаем specificity чтобы при многократном использовании компонентов,
  // в разных виджетах на одной странице,
  // первичные стили не перетирали переопределенные
  && {
    width: 160px;
    height: 100%;
    flex-shrink: 0;

    & * {
      height: 100%;
    }

    & svg {
      top: 0;
      bottom: 0;
    }

    ${({ $isSmall }) => $isSmall && 'width: 90px;'}

    & .swiper-lt-preloader {
      display: none;
    }
  }
`;

const StyledContentContainer = styled.section`
  padding-left: 8px;
`;

const StyledHotelRoomHeading = styled.h3`
  font-size: 14px;
`;

const StyledSurchargeInfo = styled.div`
  color: #919499;
  font-size: 12px;
`;

const StyledBubble = styled(Bubble)`
  // повышаем specificity чтобы при многократном использовании компонентов,
  // в разных виджетах на одной странице,
  // первичные стили не перетирали переопределенные
  && {
    &:hover {
      cursor: pointer;
    }
  }
`;

const CircleContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  margin-left: 5px;

  width: 14px;
  height: 14px;

  border: solid 1px;
  border-radius: 100%;

  color: #919499;
  text-align: center;
`;

const BubbleTextContainer = styled.div`
  font-family: 'Backpack', 'Helvetica', sans-serif;
  font-size: 12px;
`;

const StyledLabelWrapper = styled.div<{ $isMobile: boolean }>`
  & span {
    max-width: ${({ $isMobile }) => ($isMobile ? '150px' : '230px')};
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export default HotelRoomCard;
